import { FC } from 'react'

import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSelector } from 'react-redux'
import { object, string as yupString } from 'yup'
import styled from 'styled-components'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getErrorCuratorDeleteAccount } from 'src/Redux/settings-process/curatorDeleteAccount/selectors'
import { PrimaryDottedButton } from 'src/Components/Buttons/PrimaryDottedButton'
import { PrimaryInput } from 'src/Components/PrimaryInput'
import { getErrorArtistDeleteAccount } from 'src/Redux/settings-process/artistDeleteAccount/selectors'
import { getIsArtist } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { PrimaryLoading } from 'src/Components/PrimaryLoading'
import { deleteArtistAccount } from 'src/Redux/settings-process/artistDeleteAccount/api-actions'
import { deleteCuratorAccount } from 'src/Redux/settings-process/curatorDeleteAccount/api-actions'
import { getUserEmail } from 'src/Redux/auth-process/userSlice/selectors'
import { PrimaryInfoModal } from 'src/Components/PrimaryInfoModal'
import { PrimaryAlert } from 'src/Components/PrimaryAlert'
import { RedButton } from 'src/Components/Buttons/RedButton'

import { mailerLiteWebhook } from './mailerLiteWebhook'

import {
  ContentWrapper,
  FormGroup,
  FormGroupField,
  ButtonWrapper,
} from './styles'

const StyledModal = styled(PrimaryInfoModal)`
  max-width: 400px;
  .ant-modal-body {
    gap: 16px;
  }
`
interface DeleteAccountModalProps {
  toggleModalVisible: () => void
  visible: boolean
}

interface FormState {
  deletion: string
}

const DeleteAccountSchema = object({
  deletion: yupString()
    .required("Field shouldn't be empty")
    .matches(/DELETE/, 'Wrong confirmation word'),
}).required()

export const DeleteAccountModal: FC<DeleteAccountModalProps> = ({
  toggleModalVisible,
  visible,
}) => {
  const dispatch = useAppDispatch()

  const isArtist = useSelector(getIsArtist)
  const userEmail = useAppSelector(getUserEmail)
  const curatorError = useAppSelector(getErrorCuratorDeleteAccount)
  const artistError = useAppSelector(getErrorArtistDeleteAccount)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormState>({
    mode: 'onSubmit',
    defaultValues: { deletion: '' },
    resolver: yupResolver(DeleteAccountSchema),
  })

  const watchDelete = useWatch({ control, name: 'deletion' })

  const handleCancel = (): void => {
    reset()
    toggleModalVisible()
  }

  const handleDeleteAccount = (): void => {
    if (isArtist) {
      void dispatch(deleteArtistAccount())
        .unwrap()
        .then(async () => {
          await mailerLiteWebhook({
            email: userEmail,
          })
        })
    } else {
      void dispatch(deleteCuratorAccount())
        .unwrap()
        .then(async () => {
          await mailerLiteWebhook({
            email: userEmail,
          })
        })
    }
  }

  const isError = artistError || curatorError

  return (
    <StyledModal
      isOpen={visible}
      title='Delete your account'
      onClose={handleCancel}
    >
      {!isSubmitting && !isError && (
        <>
          <form
            data-testid='delete-form'
            onSubmit={handleSubmit(handleDeleteAccount)}
          >
            <ContentWrapper>
              <p>
                Are you sure you want to delete your account?
                <br />
                Please note: &nbsp; If you delete your account, you won&#39;t be
                able to reactivate it later.
              </p>
              <FormGroup>
                <FormGroupField>
                  <Controller
                    name='deletion'
                    control={control}
                    render={({ field }) => (
                      <PrimaryInput
                        {...field}
                        data-testid='delete-account-form'
                        placeholder='Type DELETE to confirm'
                        autoComplete='off'
                        error={errors.deletion?.message}
                      />
                    )}
                  />
                </FormGroupField>
              </FormGroup>
            </ContentWrapper>
          </form>
          <ButtonWrapper>
            <PrimaryDottedButton onClick={handleCancel}>
              Cancel
            </PrimaryDottedButton>
            <RedButton
              disabled={watchDelete !== 'DELETE' || isSubmitting}
              onClick={handleSubmit(handleDeleteAccount)}
            >
              Delete account
            </RedButton>
          </ButtonWrapper>
        </>
      )}
      {isSubmitting && (
        <ContentWrapper>
          <PrimaryLoading data-testid='loader' fontSize={24} />
        </ContentWrapper>
      )}
      {isError && (
        <ContentWrapper>
          <PrimaryAlert type='danger' hasCross={false}>
            Something went wrong. Please contact support team.
          </PrimaryAlert>
        </ContentWrapper>
      )}
    </StyledModal>
  )
}
